import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import ConfirmationModal from "components/ConfirmationModal";
import Header from "components/Header";
import Stepper from "components/Stepper";
import { routes } from "constants/routes";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useAnalytics } from "hooks/useAnalytics";
import MainLayout from "layouts/Main";
import { IFRAttachment, InformationFormData } from "models/HaulPay";
import { createFRLineItem, updateFRLineItem } from "services/api/haulpay";
import PayConfirmation from "./Confirmation";
import PayDocuments from "./Documents";
import { resolveCarrierFundingRequestError } from "./fundingRequestError";
import PayInformation from "./Information";
import {
  MUTATION_CREATE_CARRIER_FUNDING_REQUEST,
  MUTATION_UPDATE_CARRIER_FUNDING_REQUEST
} from "./query";
import styles from "./style.module.scss";
import dollarsToCents from "helpers/dollarsToCents";

interface ISectionProps {
  step: number;
  informationFormData?: InformationFormData;
  uploadedFiles: IFRAttachment[];
  setStepCallback: (step: number) => void;
  setIsShowSearch: (show: boolean) => void;
  setUploadedFiles: (files: IFRAttachment[]) => void;
  setFormDataCallback: (data?: InformationFormData, isValid?: boolean) => void;
  createFundingRequest: (
    complete: boolean,
    step: number,
    data?: InformationFormData
  ) => Promise<void>;
  createdFR: any;
}

function SelectedSection({
  step,
  informationFormData,
  uploadedFiles,
  setIsShowSearch,
  setStepCallback,
  setUploadedFiles,
  setFormDataCallback,
  createFundingRequest,
  createdFR
}: Readonly<ISectionProps>) {
  const navigate = useNavigate();

  switch (step) {
    case 0:
      return (
        <PayInformation
          informationFormData={informationFormData}
          handleNext={async (data) => {
            await createFundingRequest(false, step, data);
          }}
          setIsShowSearch={setIsShowSearch}
          setFormDataCallback={setFormDataCallback}
        />
      );

    case 1:
      return (
        <PayDocuments
          createdFR={createdFR}
          handleNext={() => setStepCallback(step + 1)}
          handleBack={() => {
            setFormDataCallback();
            setStepCallback(0);
          }}
          informationFormData={informationFormData}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
        />
      );

    case 2:
      return (
        <PayConfirmation
          createdFR={createdFR}
          informationFormData={informationFormData}
          handleNext={() => navigate(routes.HAULPAY_CARRIER_MANAGE_PAY)}
        />
      );

    default:
      return null;
  }
}

export default function CarrierRequestPay() {
  const [step, setStep] = useState(0);
  const [isShowSearch, setIsShowSearch] = useState(false);
  const [informationFormData, setInformationFormData] = useState<InformationFormData>();
  const [uploadedFiles, setUploadedFiles] = useState<IFRAttachment[]>([]);
  const [createdFR, setCreatedFR] = useState<{
    id: string;
    lineItemsId?: string;
  }>();
  const [isWarningModalOpen, toggleWarningModal] = useToggle();
  const { trackAllEvents } = useAnalytics();

  const handleFundingRequestError = useCallback(
    (err: unknown) => {
      const { showDuplicateLoadModal, message, raw } = resolveCarrierFundingRequestError(err);

      if (showDuplicateLoadModal) {
        toggleWarningModal();
        return;
      }

      Sentry.captureException(raw);
      showErrorMessage(message);
    },
    [toggleWarningModal]
  );

  const [createFR] = useMutation(MUTATION_CREATE_CARRIER_FUNDING_REQUEST, {
    onError: handleFundingRequestError
  });

  const [updateFR] = useMutation(MUTATION_UPDATE_CARRIER_FUNDING_REQUEST, {
    onError: handleFundingRequestError
  });

  const setFormDataCallback = (data?: InformationFormData) => {
    setInformationFormData(data);
  };

  const successMessage = (isCompleted?: boolean) => {
    if (isCompleted) return "Funding Request Created";
    else if (createdFR?.id) return "Funding Request Updated";
    else return "Incomplete Funding Request Created";
  };

  const createFundingRequest = useCallback(
    async (complete: boolean, step: number, data?: InformationFormData) => {
      let fundingRequestFormData: InformationFormData | undefined;

      if (data) {
        fundingRequestFormData = data;
      } else if (informationFormData) {
        fundingRequestFormData = informationFormData;
      }

      if (fundingRequestFormData) {
        const submittedData = {
          ...(createdFR?.id && { id: createdFR.id }),
          amount: dollarsToCents(fundingRequestFormData.totalInvoiceAmount),
          debtor: fundingRequestFormData.billToCompany?.id,
          billToCompanyPhone: fundingRequestFormData.billToCompany?.phone_number,
          billToCompanyEmail: fundingRequestFormData.billToCompany?.email,
          streetOne: fundingRequestFormData.billToCompany?.address.street_one,
          city: fundingRequestFormData.billToCompany?.address.city,
          state: fundingRequestFormData.billToCompany?.address.state,
          zip: fundingRequestFormData.billToCompany?.address.zip,
          country: fundingRequestFormData.billToCompany?.address.country,
          userLoadNumber: fundingRequestFormData.loadNumber,
          fuelAdvanceAmount:
            fundingRequestFormData.fuelAdvanceAmount &&
            dollarsToCents(fundingRequestFormData.fuelAdvanceAmount),
          nonFactored: fundingRequestFormData.isNonFactoredInvoice,
          autoInvoice: !!fundingRequestFormData.isNonFactoredInvoice,
          userReferenceNumber: fundingRequestFormData.invoiceNumber,
          userNotes: fundingRequestFormData.invoiceDetail
        };

        const res = createdFR?.id
          ? await updateFR({
              variables: { ...submittedData }
            })
          : await createFR({
              variables: { ...submittedData }
            });

        if (!res?.errors) {
          const fundingRequestId = res.data.createOrUpdateCarrierFundingRequest.fundingRequest.id;
          setCreatedFR((prev) => ({
            id: fundingRequestId,
            lineItemsId: prev?.lineItemsId
          }));

          let lineItemsResponse: any;
          if (fundingRequestFormData.advanceAmount && fundingRequestFormData.advanceAmount > 0) {
            const advanceAmount = dollarsToCents(fundingRequestFormData.advanceAmount);
            try {
              lineItemsResponse = !createdFR?.lineItemsId
                ? await createFRLineItem({
                    amount_requested: advanceAmount,
                    fundingRequestId
                  })
                : await updateFRLineItem({
                    amount_requested: advanceAmount,
                    lineItemId: createdFR?.lineItemsId,
                    fundingRequestId
                  });
            } catch (error: any) {
              const data = error?.response?.data;
              showErrorMessage(
                (Array.isArray(data) ? data[0] : data?.detail) ||
                  "Unable to add the debtor advance. Please try again."
              );
              return;
            }
          }

          setCreatedFR((prev) => ({
            id: fundingRequestId,
            lineItemsId: lineItemsResponse?.data?.id ?? prev?.lineItemsId
          }));
          if (complete) {
            trackAllEvents("carrier_funding_request_created", {
              amount: submittedData.amount,
              non_factored: submittedData.nonFactored ? "true" : "false"
            });
          }
          showSuccessMessage(successMessage(complete));
          setStep(step + 1);
        }
      }
    },
    [informationFormData, setStep, createdFR]
  );

  return (
    <MainLayout
      desktopTitle="Request Pay"
      customHeader={<Header label="Request Pay" isHideDesktop />}
      className="flex-1 px-5">
      <div className={clsx(styles.requestPayStepperContainer, isShowSearch && "hidden")}>
        <div className={styles.body}>
          <h4 className={styles.subtitle}>Fill the below form to request payment</h4>
          <Stepper
            data={Array.from({ length: 3 }).map((_, i) => ({ id: i }))}
            currentStep={step}
            setStep={setStep}
            className="z-10 mb-7"
            canNavigate={false}
          />
        </div>

        <SelectedSection
          step={step}
          informationFormData={informationFormData}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          setStepCallback={setStep}
          setFormDataCallback={setFormDataCallback}
          setIsShowSearch={setIsShowSearch}
          createFundingRequest={createFundingRequest}
          createdFR={createdFR}
        />
      </div>

      <ConfirmationModal
        title="Warning"
        isConfirmModalOpen={isWarningModalOpen}
        toggleConfirmModal={toggleWarningModal}
        isHideCancelButton
        content="This load number already has a funding request associated with it. Please go to manage pay to search for and edit your previous funding request that is already using this load number."
      />
    </MainLayout>
  );
}
